import * as React from "react"
import pkg from "react-lazy-load-image-component"
import type { LazyLoadImageProps } from "react-lazy-load-image-component"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"

const { LazyLoadImage } = pkg

interface LazyImageProps extends Omit<LazyLoadImageProps, 'src' | 'alt'> {
  /**
   * The source URL of the image
   */
  src: string
  /**
   * Alternative text for the image
   */
  alt: string
  /**
   * Custom loading component to show while image is loading
   */
  loadingComponent?: React.ReactNode
  /**
   * Custom error component to show when image fails to load
   */
  errorComponent?: React.ReactNode
  /**
   * Threshold for intersection observer (0-1)
   * @default 0.1
   */
  threshold?: number
  /**
   * Root margin for intersection observer
   * @default "0px"
   */
  rootMargin?: string
  /**
   * Callback fired when image starts loading
   */
  onLoadStart?: () => void
  /**
   * Callback fired when image loads successfully
   */
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void
  /**
   * Callback fired when image fails to load
   */
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void
  /**
   * Effect to apply when image loads
   * @default "opacity"
   */
  effect?: "blur" | "black-and-white" | "opacity"
  /**
   * Placeholder source for effects
   */
  placeholderSrc?: string
  /**
   * Whether to use intersection observer
   * @default true
   */
  useIntersectionObserver?: boolean
  /**
   * Whether the image should be visible by default
   * @default false
   */
  visibleByDefault?: boolean
}

const LazyImage = React.forwardRef<HTMLImageElement, LazyImageProps>(
  (
    {
      className,
      src,
      alt,
      loadingComponent,
      errorComponent,
      threshold = 0.1,
      rootMargin = "0px",
      onLoadStart,
      onLoad,
      onError,
      effect = "opacity",
      placeholderSrc,
      useIntersectionObserver = true,
      visibleByDefault = false,
      placeholder,
      ...props
    },
    ref
  ) => {
    const isServer = typeof window === 'undefined'
    const [hasError, setHasError] = React.useState(false)

    if (isServer) {
      return (
        <img
          ref={ref as React.Ref<HTMLImageElement>}
          src={src}
          alt={alt}
          className={cn("rounded-lg object-cover", className)}
          width={props.width as number | undefined}
          height={props.height as number | undefined}
        />
      )
    }

    // Handle image error
    const handleError = React.useCallback(
      (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setHasError(true)
        onError?.(event)
      },
      [onError]
    )

    // Handle load start
    const handleBeforeLoad = React.useCallback(() => {
      onLoadStart?.()
    }, [onLoadStart])

    // Show error component if image failed to load
    if (hasError && errorComponent) {
      return <>{errorComponent}</>
    }

    // Custom placeholder component
    const customPlaceholder = loadingComponent || (
      <Skeleton
        data-slot="lazy-image-skeleton"
        className={cn("animate-pulse", className)}
        style={{
          width: props.width,
          height: props.height,
        }}
      />
    )

    return (
      <LazyLoadImage
        data-slot="lazy-image"
        src={src}
        alt={alt}
        effect={effect}
        placeholderSrc={placeholderSrc}
        placeholder={placeholder as React.ReactElement || customPlaceholder}
        threshold={threshold * 100} // Convert to pixels (0.1 -> 10px)
        useIntersectionObserver={useIntersectionObserver}
        visibleByDefault={visibleByDefault}
        wrapperProps={{
          className: cn(
            "transition-opacity duration-300 rounded-lg object-cover",
          ),
        }}
        className={cn("rounded-lg object-cover", className)}
        onLoad={onLoad}
        onError={handleError}
        beforeLoad={handleBeforeLoad}
        {...props}
      />
    )
  }
)

LazyImage.displayName = "LazyImage"

export { LazyImage }
export type { LazyImageProps }
